import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { AltRouteOutlined } from "@mui/icons-material";

import AssistantAvatar from "../../../views/assistant/components/ui/AssistantAvatar";
import AssistantMessageActions from "./AssistantMessageActions";
import { AssistantPart, isReaderPart } from "./AssistantPart";
import AssistantTextContainer from "./AssistantTextContainer";
import {
  ChatMessageWithParts,
  ContentType,
  TMessageContentPart,
} from "src/views/assistant/types";
import { DPO_FEEDBACK_TYPE } from "src/constants";
import SelectResponseButton from "./SelectResponseButton";
import SubAgentActivity from "./artifacts/SubAgentActivity";
import ToolRunGroup from "./artifacts/ToolRunGroup";
import WorkingIndicator, { NoReplyLine } from "./WorkingIndicator";
import {
  extractToolCallId,
  resolveEffectivePartType,
} from "src/reusable/Chat/ChatStreaming/services/contentTypeUtils";
import {
  isInteractiveToolResponse,
  isToolResponseTextComplete,
  parseToolCallInfo,
  parseToolResponseText,
} from "./artifacts/toolStatusUtils";
import { Fragment, memo, useMemo } from "react";
import { useChatContext } from "../context/ChatContext";
import { parsePayloadObject } from "src/reusable/Chat/ChatStreaming/services/contentTypeUtils";

import {
  getRoutingContextLabel,
  getRoutingChangeLabel,
} from "src/views/assistant/routing/routingDisplayUtils";

/** Minimum completed tool calls in a run before it rolls up into one line */
const MIN_TOOLS_TO_ROLLUP = 3;

type RenderItem = { part: TMessageContentPart; idx: number };
type GroupToolInfo = { name: string; statusMessage?: string };
type RenderSegment =
  | { kind: "parts"; items: RenderItem[] }
  | {
      kind: "toolGroup";
      items: RenderItem[];
      callCount: number;
      tools: GroupToolInfo[];
    };

/** The wrapper `tool_call_id` a part is linked to, if any. */
const getToolCallId = (part: TMessageContentPart): string | null => {
  const payload = parsePayloadObject(part.text);
  return payload && typeof payload.tool_call_id === "string"
    ? payload.tool_call_id
    : null;
};

type AssistantMessageProps = {
  chatMessage: ChatMessageWithParts;
  dialogue: ChatMessageWithParts[];
  sortedMessageGroup: ChatMessageWithParts[];
  messageGroupIdx: number;
  messageIndex: number;
  currentIndex: number;
  isChatConnected: boolean;
  /** A turn is running, which a connected multi-turn stream does not imply. */
  isTurnActive?: boolean;
  isChatMessageStreamStopped: boolean;
  isPreferredMessagePicked: boolean;
  regenerateResponse: (index: string) => void;
  setAsPreferredMessage: (id: string) => void;
  handlePrev: () => void;
  handleNext: () => void;
};

const AssistantMessage = ({
  chatMessage,
  dialogue,
  sortedMessageGroup,
  messageGroupIdx,
  messageIndex,
  currentIndex,
  isChatConnected,
  isTurnActive = false,
  isChatMessageStreamStopped,
  isPreferredMessagePicked = false,
  regenerateResponse,
  setAsPreferredMessage,
  handlePrev,
  handleNext,
}: AssistantMessageProps) => {
  const {
    AssistantTextMetadata,
    AssistantTextMetadataProps,
    activeSubAgents,
    compactToolCalls,
    transcriptOnly = false,
    chatErrorShown = false,
  } = useChatContext();
  const theme = useTheme();
  const lessThanMdBreakpoint = useMediaQuery(theme.breakpoints.down("md"));

  // Extract previous assistant message's routing info for change detection.
  // Uses dialogue.length as dep instead of the dialogue array ref to avoid
  // recomputing on every streaming chunk (array gets new ref each update).
  const prevAssistantRouting = useMemo(() => {
    const assistantMessages = dialogue.filter((m) => m.type === "Assistant");
    const myIndex = assistantMessages.findIndex((m) => m.id === chatMessage.id);
    if (myIndex <= 0) return { isFirst: myIndex === 0, slug: null };
    return {
      isFirst: false,
      slug: assistantMessages[myIndex - 1]?.routing?.persona_slug ?? null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMessage.id, dialogue.length]);

  // Every tool_call_id in the dialogue, so a card whose part hasn't streamed
  // yet is told apart from one anchored to an earlier turn. Keyed on part
  // count to recompute when a tool call arrives, not on every text delta.
  const lastMsgPartCount =
    dialogue[dialogue.length - 1]?.contentParts?.length ?? 0;
  const allToolCallIds = useMemo(() => {
    const ids = new Set<string>();
    for (const msg of dialogue) {
      for (const part of msg.contentParts ?? []) {
        const payload = parsePayloadObject(part.text);
        if (payload && typeof payload.tool_call_id === "string") {
          ids.add(payload.tool_call_id);
        }
      }
    }
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogue.length, lastMsgPartCount]);

  const routingLabel = useMemo(() => {
    const routing = chatMessage.routing;
    if (!routing || transcriptOnly) return null;
    if (routing.access_denied) return null;

    if (prevAssistantRouting.isFirst) {
      // First assistant message — always show context label
      return getRoutingContextLabel(routing);
    }

    // Subsequent messages — only show when persona changed
    if (prevAssistantRouting.slug !== routing.persona_slug) {
      return getRoutingChangeLabel(routing);
    }

    return null;
  }, [chatMessage.routing, prevAssistantRouting, transcriptOnly]);

  // What a transcript-only reader is shown: prose or an interactive result.
  const hasReaderContent = useMemo(() => {
    if (!transcriptOnly) return true;
    return (chatMessage.contentParts ?? []).some((part) =>
      isReaderPart(part, isChatConnected),
    );
  }, [transcriptOnly, chatMessage.contentParts, isChatConnected]);

  // Compact mode: roll runs of consecutive completed tool calls up into a
  // single collapsible "Used N tools" group. Returns null (plain part map)
  // when the mode is off or nothing qualifies for grouping.
  const toolSegments = useMemo<RenderSegment[] | null>(() => {
    const parts = chatMessage.contentParts;
    if (!compactToolCalls || transcriptOnly || !parts?.length) return null;

    const types = parts.map((part) => resolveEffectivePartType(part));
    const isBlankText = (i: number) =>
      types[i] === ContentType.TEXT && !(parts[i].text ?? "").trim();

    // Id-matched: with parallel calls, position-before-last-response ≠ done.
    const respondedCallIds = new Set<string>();
    parts.forEach((part, idx) => {
      if (types[idx] !== ContentType.TOOL_RESPONSE) return;
      const id = extractToolCallId(part.text);
      if (id) respondedCallIds.add(id);
    });
    const callIsResolved = (i: number) => {
      const id = extractToolCallId(parts[i].text);
      return id ? respondedCallIds.has(id) : false;
    };

    const segments: RenderSegment[] = [];
    let run: RenderItem[] = [];

    const flushRun = () => {
      if (!run.length) return;
      // While streaming, only the fully resolved prefix groups: an awaiting
      // call, or live thinking past the last response, stays visible.
      let tailStart = -1;
      if (isChatConnected && !isChatMessageStreamStopped) {
        for (let i = 0; i < run.length; i++) {
          if (
            types[run[i].idx] === ContentType.TOOL_CALL &&
            !callIsResolved(run[i].idx)
          ) {
            tailStart = i;
            break;
          }
        }
        if (tailStart === -1) {
          let lastResponse = -1;
          for (let i = run.length - 1; i >= 0; i--) {
            if (types[run[i].idx] === ContentType.TOOL_RESPONSE) {
              lastResponse = i;
              break;
            }
          }
          for (let i = lastResponse + 1; i < run.length; i++) {
            if (!isBlankText(run[i].idx)) {
              tailStart = i;
              break;
            }
          }
        }
      }
      const grouped = tailStart === -1 ? run : run.slice(0, tailStart);
      const tail = tailStart === -1 ? [] : run.slice(tailStart);
      const callCount = grouped.filter(
        (item) => types[item.idx] === ContentType.TOOL_CALL,
      ).length;
      if (callCount >= MIN_TOOLS_TO_ROLLUP) {
        const tools = grouped
          .filter((item) => types[item.idx] === ContentType.TOOL_CALL)
          .map((item) => parseToolCallInfo(item.part.text ?? ""))
          .filter(Boolean) as GroupToolInfo[];
        segments.push({ kind: "toolGroup", items: grouped, callCount, tools });
        if (tail.length) segments.push({ kind: "parts", items: tail });
      } else {
        segments.push({ kind: "parts", items: run });
      }
      run = [];
    };

    parts.forEach((part, idx) => {
      const type = types[idx];
      if (type === ContentType.TOOL_CALL) {
        run.push({ part, idx });
        return;
      }
      if (type === ContentType.TOOL_RESPONSE) {
        // Interactive results (prompts, downloads, previews) render their
        // own UI and must never fold into a collapsed group. Pull their
        // tool call back out of the run and emit the pair individually.
        const payload = isToolResponseTextComplete(
          part.text ?? "",
          isChatConnected,
        )
          ? parseToolResponseText(part.text ?? "")
          : null;
        if (isInteractiveToolResponse(payload)) {
          // Find the owning call by id — in a parallel batch the most recent
          // call may be an unrelated sibling. Splice only that call out.
          const respId = extractToolCallId(part.text);
          let pulled: RenderItem[] = [];
          if (respId) {
            const callPos = run.findIndex(
              (item) =>
                types[item.idx] === ContentType.TOOL_CALL &&
                extractToolCallId(parts[item.idx].text) === respId,
            );
            if (callPos !== -1) pulled = run.splice(callPos, 1);
          } else {
            // Legacy id-less parts: fall back to the most recent call.
            let callPos = run.length - 1;
            while (
              callPos >= 0 &&
              (isBlankText(run[callPos].idx) ||
                types[run[callPos].idx] === ContentType.THINK)
            ) {
              callPos--;
            }
            if (
              callPos >= 0 &&
              types[run[callPos].idx] === ContentType.TOOL_CALL
            ) {
              pulled = run.splice(callPos);
            }
          }
          flushRun();
          segments.push({ kind: "parts", items: [...pulled, { part, idx }] });
          return;
        }
        run.push({ part, idx });
        return;
      }
      // Thinking between tool calls is part of the run — fold it into the
      // rollup instead of splitting it (a leading thought before any call
      // still renders outside). Whitespace-only TEXT parts render nothing —
      // let them ride along too.
      if (run.length && (type === ContentType.THINK || isBlankText(idx))) {
        run.push({ part, idx });
        return;
      }
      flushRun();
      segments.push({ kind: "parts", items: [{ part, idx }] });
    });
    flushRun();

    return segments.some((segment) => segment.kind === "toolGroup")
      ? segments
      : null;
  }, [
    compactToolCalls,
    transcriptOnly,
    chatMessage.contentParts,
    isChatConnected,
    isChatMessageStreamStopped,
  ]);

  const isFeedbackDpo = chatMessage?.ranking_type === DPO_FEEDBACK_TYPE;

  const isRenderSelectButton =
    !isChatConnected &&
    isChatMessageStreamStopped &&
    isFeedbackDpo &&
    (chatMessage?.feedback_value === null ||
      chatMessage?.feedback_value === undefined);

  const isRenderResponseIndexText = isFeedbackDpo && !isPreferredMessagePicked;

  const renderPart = (part: TMessageContentPart, idx: number) => (
    <AssistantPart
      key={idx}
      part={part}
      partIndex={idx}
      chatMessage={chatMessage}
      messageIndex={messageIndex}
      isChatConnected={isChatConnected}
      isChatMessageStreamStopped={isChatMessageStreamStopped}
    />
  );

  // A reply with nothing for the reader works while its turn runs, then says no reply came through.
  if (transcriptOnly && !hasReaderContent) {
    const at = dialogue.indexOf(chatMessage);
    const later = at === -1 ? [] : dialogue.slice(at + 1);
    const nextUser = later.findIndex((message) => message.type === "User");
    const sameTurn = nextUser === -1 ? later : later.slice(0, nextUser);
    // A later reply in its turn, or a later fragment of it after a send mid-turn, speaks for it.
    if (
      (chatMessage.id &&
        later.some(
          (message) =>
            message.type === "Assistant" && message.id === chatMessage.id,
        )) ||
      sameTurn.some((message) => message.type === "Assistant")
    ) {
      return null;
    }
    const isLatestTurn = nextUser === -1;
    const isRunning =
      isLatestTurn && isTurnActive && !isChatMessageStreamStopped;
    if (!isRunning && isLatestTurn && chatErrorShown) return null;
    return (
      <Box
        id={`chat-bubble-container-${chatMessage.type}-${messageIndex}`}
        data-testid={`aa-msg-assistant-${messageGroupIdx}`}
        className={`chat-bubble-container ${chatMessage.type}`}
      >
        {!isRunning && isRenderResponseIndexText && (
          <Typography variant="body1" sx={{ color: "text.secondary", mb: 1 }}>
            Response {messageIndex + 1}
          </Typography>
        )}
        <Stack direction="row" gap={2} alignItems="center">
          <AssistantAvatar />
          {isRunning ? <WorkingIndicator /> : <NoReplyLine />}
        </Stack>
        {!isRunning && isRenderSelectButton && (
          <Box sx={{ mt: 1.5 }}>
            <SelectResponseButton
              chatMessage={chatMessage}
              dialogue={dialogue}
              setAsPreferredMessage={setAsPreferredMessage}
            />
          </Box>
        )}
      </Box>
    );
  }

  if (chatMessage.contentParts)
    return (
      <Box>
        <Box
          key={messageIndex}
          id={`chat-bubble-container-${chatMessage.type}-${messageIndex}`}
          data-testid={`aa-msg-assistant-${messageGroupIdx}`}
          className={`chat-bubble-container ${chatMessage.type}`}
        >
          {isRenderResponseIndexText && (
            <Stack
              direction={lessThanMdBreakpoint ? "column" : "row"}
              gap={2}
              alignItems="flex-start"
              sx={{ mb: 1.5 }}
            >
              <AssistantAvatar />
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                Response {messageIndex + 1}
              </Typography>
            </Stack>
          )}
          {routingLabel && (
            <Typography
              data-testid="aa-routing-context"
              variant="caption"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontStyle: "italic",
                color: "primary.main",
                opacity: 0.7,
                mb: 0.5,
                ml: lessThanMdBreakpoint ? 0 : 7,
                animation: "fadeInRouting 300ms ease-in",
                "@keyframes fadeInRouting": {
                  from: { opacity: 0 },
                  to: { opacity: 0.7 },
                },
              }}
            >
              <AltRouteOutlined sx={{ fontSize: 14 }} />
              {routingLabel}
            </Typography>
          )}
          <Stack
            direction={lessThanMdBreakpoint ? "column" : "row"}
            gap={2}
            alignItems="flex-start"
          >
            {!isRenderResponseIndexText && <AssistantAvatar />}
            <Box
              id={`chat-bubble-content-${chatMessage.type}-${messageIndex}`}
              data-testid={`aa-msg-assistant-content-${messageGroupIdx}`}
              className="chat-bubble-content"
            >
              <AssistantTextContainer
                messageIndex={messageIndex}
                chatMessage={chatMessage}
              >
                {/* Render content parts — optionally rolled up into compact
                    tool-run groups — and render sub-agent activity cards
                    INLINE after the LAST part carrying the spawning
                    delegate_task tool_call_id: after the response row once
                    it exists, directly after the tool-call card while still
                    streaming. Cards anchor to whichever message owns their
                    spawning tool call, so completed sub-agents stay attached
                    to their turn when later messages arrive. Cards always
                    render OUTSIDE ToolRunGroup rollups so a collapsed group
                    never hides sub-agent activity. Only the last (streaming)
                    message hosts the unmatched fallback, and only for
                    still-running agents with no anchor anywhere in the
                    dialogue yet (covers both the live in-progress-streaming
                    case and a hydrated/restored card from a reload) — a
                    completed unmatched card is a stale leftover from an
                    earlier turn and must not re-attach. */}
                {(() => {
                  const isLastMessage =
                    dialogue[dialogue.length - 1] === chatMessage;
                  // A transcript-only reader sees none of the work behind a reply.
                  const subs = transcriptOnly ? [] : (activeSubAgents ?? []);
                  const rendered = new Set<number>();
                  const cardsForToolCall = (toolCallId: string | null) => {
                    if (!toolCallId) return [];
                    const matched = subs.filter(
                      (sa) =>
                        sa.parentToolCallId === toolCallId &&
                        !rendered.has(sa.dialogueId),
                    );
                    matched.forEach((sa) => rendered.add(sa.dialogueId));
                    return matched;
                  };
                  const renderCard = (sa: (typeof subs)[number]) => (
                    <SubAgentActivity
                      key={sa.dialogueId}
                      personaName={sa.personaName}
                      status={sa.status}
                      messages={sa.messages}
                      startedAt={sa.startedAt}
                      endedAt={sa.endedAt}
                    />
                  );
                  const parts = chatMessage.contentParts ?? [];
                  // A tool_call_id can appear on more than one part in the
                  // same message (the call, then its response) — anchor to
                  // the LAST occurrence so the card lands after the response
                  // row once it exists, not stranded on the call row.
                  const partToolCallIds = parts.map((part) =>
                    getToolCallId(part),
                  );
                  const lastIndexForToolCallId = new Map<string, number>();
                  partToolCallIds.forEach((id, idx) => {
                    if (id) lastIndexForToolCallId.set(id, idx);
                  });
                  const cardsForPartIndex = (idx: number) => {
                    const toolCallId = partToolCallIds[idx];
                    if (
                      toolCallId === null ||
                      lastIndexForToolCallId.get(toolCallId) !== idx
                    ) {
                      return [];
                    }
                    return cardsForToolCall(toolCallId);
                  };
                  const renderPartWithCards = (
                    part: TMessageContentPart,
                    idx: number,
                  ) => (
                    <Fragment key={idx}>
                      {renderPart(part, idx)}
                      {cardsForPartIndex(idx).map(renderCard)}
                    </Fragment>
                  );
                  return (
                    <>
                      {toolSegments
                        ? toolSegments.map((segment) =>
                            segment.kind === "toolGroup" ? (
                              <Fragment key={`group-${segment.items[0].idx}`}>
                                <ToolRunGroup
                                  callCount={segment.callCount}
                                  tools={segment.tools}
                                  summaryEnabled={
                                    !isChatConnected ||
                                    isChatMessageStreamStopped
                                  }
                                >
                                  {segment.items.map(({ part, idx }) =>
                                    renderPart(part, idx),
                                  )}
                                </ToolRunGroup>
                                {segment.items
                                  .flatMap(({ idx }) => cardsForPartIndex(idx))
                                  .map(renderCard)}
                              </Fragment>
                            ) : (
                              <Fragment key={`parts-${segment.items[0].idx}`}>
                                {segment.items.map(({ part, idx }) =>
                                  renderPartWithCards(part, idx),
                                )}
                              </Fragment>
                            ),
                          )
                        : parts.map((part, idx) =>
                            renderPartWithCards(part, idx),
                          )}
                      {isLastMessage &&
                        subs
                          .filter(
                            (sa) =>
                              !rendered.has(sa.dialogueId) &&
                              // Only cards with no anchor yet anywhere in the
                              // dialogue; ones tied to an earlier turn (or
                              // already attached above) stay there.
                              (!sa.parentToolCallId ||
                                !allToolCallIds.has(sa.parentToolCallId)) &&
                              // A completed/errored card with no anchor is a
                              // stale leftover, not live work — don't let it
                              // re-attach to whatever is now the last message.
                              sa.status !== "completed" &&
                              sa.status !== "error",
                          )
                          .map(renderCard)}
                    </>
                  );
                })()}
              </AssistantTextContainer>
              {AssistantTextMetadata && (
                <AssistantTextMetadata
                  messageId={chatMessage?.id}
                  {...AssistantTextMetadataProps}
                />
              )}
              {!transcriptOnly && (
                <AssistantMessageActions
                  isChatConnected={isChatConnected}
                  chatMessage={chatMessage}
                  dialogue={dialogue}
                  currentIndex={currentIndex}
                  sortedMessageGroup={sortedMessageGroup}
                  isChatMessageStreamStopped={isChatMessageStreamStopped}
                  regenerateResponse={regenerateResponse}
                  handlePrev={handlePrev}
                  handleNext={handleNext}
                />
              )}
              {isRenderSelectButton && (
                <Box sx={{ mt: 1.5 }}>
                  <SelectResponseButton
                    chatMessage={chatMessage}
                    dialogue={dialogue}
                    setAsPreferredMessage={setAsPreferredMessage}
                  />
                </Box>
              )}
            </Box>
          </Stack>
        </Box>
      </Box>
    );
};

export default memo(AssistantMessage);
